import React, { Component } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrophy } from '@fortawesome/free-solid-svg-icons'

import Constants from '../../Constants';
import Grid from '../Grid';
import Scoring from '../../Scoring';
import Seasons from '../../Seasons';
import Utils from '../../Utils';

import '../../styles/modals/SeasonWelcomeModal.css';

// What a season can introduce, keyed by the parameter that switches it on. The
// example is a one-row board drawn by the game's own Grid, so it is highlighted
// and marked exactly as a real board would be rather than by a picture of one.
// `inSentence` is the name as it reads mid-sentence, where the mode's title
// case would not.
const INTRODUCTIONS = {
  wraparoundMode: {
    name: Constants.MODE_NAME_WRAPAROUND,
    inSentence: "Wraparound mode",
    description: Constants.MESSAGE_WRAPAROUND_MODE,
    example: [[["A"], ["P"], [], ["W"], ["R"]]]
  }
};

// The welcome to a new season, shown once on the first daily a player starts
// in it: who won the season before, and the twist this one brings. Driven by
// its entry in Constants.ANNOUNCEMENTS, so the next season's welcome is another
// entry rather than another modal.
class SeasonWelcomeModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      champions: null
    };
  }

  componentDidMount() {
    this.fetchChampions();
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  // Fetched here rather than taken from the scoreboard's "last season", which
  // is whichever season preceded the day the page was loaded: a tab left open
  // across the boundary would still be holding the one before. A finished
  // season is public, so this needs no token.
  fetchChampions = async () => {
    const previous = this.props.announcement.season - 1;
    if (previous < 1) {
      return;
    }

    try {
      const response = await fetch(`/api/seasons/${previous}/standings`);
      const json = await response.json();
      if (!json.success || this.unmounted) {
        return;
      }

      this.setState({ champions: SeasonWelcomeModal.getChampions(json.data.standings) });
    } catch (err) {
      // The section is left out; the rest of the welcome still stands.
    }
  }

  // Everybody holding a trophy, so a tie for third lists both rather than
  // whichever the standings happened to put first.
  static getChampions = (standings) => {
    const accolades = Utils.getAccoladeMap(standings);

    return standings
      .filter((standing) => standing.name in accolades)
      .map((standing) => ({ name: standing.name, score: standing.score, rank: accolades[standing.name] }));
  }

  // "Thursdays", "Tuesdays and Sundays", "Mondays, Wednesdays, and Fridays".
  static getDaysString = (seasonNumber, field) => {
    const days = Seasons.getSchedule(seasonNumber)
      .map((parameters, i) => parameters[field] ? `${Constants.DAYS[(i + 1) % 7]}s` : null)
      .filter(Boolean);

    if (days.length <= 2) {
      return days.join(" and ");
    }

    return `${days.slice(0, -1).join(", ")}, and ${days[days.length - 1]}`;
  }

  renderChampions = () => {
    const champions = this.state.champions;
    if (!champions || champions.length === 0) {
      return null;
    }

    return (
      <div className="season-welcome-modal-section">
        <div className="season-welcome-modal-paragraph">
          First, congratulations to our Season {this.props.announcement.season - 1} champions:
        </div>
        <div className="season-welcome-modal-champions">
          { champions.map((champion) => (
            <div
              className={"season-welcome-modal-champion"
                + (champion.name === this.props.loggedInPlayerName ? " season-welcome-modal-champion-self" : "")}
              key={champion.name}
            >
              <FontAwesomeIcon
                icon={faTrophy}
                className={`accolade-icon season-welcome-modal-trophy-${Constants.ACCOLADE_COLORS[champion.rank]}`}
              />
              <span className="season-welcome-modal-champion-name">{champion.name}</span>
              <span className="season-welcome-modal-champion-score">{champion.score}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderExample = (letters) => {
    const { rowData, columnData } = Scoring.calculateBoardData(letters, [], [], true);

    return (
      <div className="season-welcome-modal-example">
        <Grid
          letters={letters}
          rowData={rowData}
          columnData={columnData}
          startLetters={{}}
          expansions={[]}
          bonusCells={[]}
          onCellSelection={() => {}}
        />
      </div>
    );
  }

  renderIntroduction = () => {
    const field = this.props.announcement.introduces;
    const introduction = INTRODUCTIONS[field];
    if (!introduction) {
      return null;
    }

    const days = SeasonWelcomeModal.getDaysString(this.props.announcement.season, field);

    return (
      <div className="season-welcome-modal-section">
        <div className="season-welcome-modal-paragraph">
          On to Season {this.props.announcement.season}! This season introduces a new mode…
        </div>
        <div className="season-welcome-modal-paragraph">
          <strong>{introduction.name}</strong>: {introduction.description}
        </div>
        { introduction.example && this.renderExample(introduction.example) }
        { days &&
          <div className="season-welcome-modal-paragraph">
            {introduction.inSentence} shows up on {days}—feel free to practice in Free Play mode until then.
          </div>
        }
      </div>
    );
  }

  render() {
    const season = Seasons.getSeason(this.props.announcement.season);

    return (
      <div className="season-welcome-modal">
        <div className="season-welcome-modal-title">
          Welcome to Season {season.number}!
        </div>
        <div className="season-welcome-modal-season">
          {season.startDate} to {season.endDate}
        </div>

        { this.renderChampions() }
        { this.renderIntroduction() }

        <div className="season-welcome-modal-paragraph">
          Good luck in Season {season.number}!
        </div>

        <button className="season-welcome-modal-close" onClick={this.props.onClose}>
          Close
        </button>
      </div>
    );
  }
}

export default SeasonWelcomeModal;
